import { useEffect, useRef, useState } from 'react';
import { probeVideoDuration } from '@/lib/durationProbe';
import { isYouTubeUrl, type VideoData } from '@/lib/videoTypes';

export type ProbedDurationState =
  | { status: 'known'; duration: number }
  | { status: 'probing' }
  | { status: 'unknown' };

/**
 * Determine a video's duration when the event itself doesn't carry one.
 *
 * Probing starts only when `enabled` is true (e.g. the card scrolled into
 * view) so feeds don't fire off dozens of media requests at once. Results
 * are cached per URL by the probe library.
 *
 * - Direct media files: read via a hidden <video preload="metadata">.
 * - HLS (m3u8): playlist text is fetched and EXTINF durations summed
 *   (master playlists resolved recursively).
 * - YouTube links: never determinable client-side.
 */
export function useProbedDuration(video: VideoData, enabled: boolean): ProbedDurationState {
  const [state, setState] = useState<ProbedDurationState>(() =>
    video.duration ? { status: 'known', duration: video.duration } : { status: 'probing' },
  );
  const startedRef = useRef(false);

  useEffect(() => {
    if (video.duration) {
      setState({ status: 'known', duration: video.duration });
      return;
    }
    if (!enabled || startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;
    probeVideoDuration(video.videoUrl, video.mimeType).then((result) => {
      if (cancelled) return;
      if (result.status === 'ok') {
        setState({ status: 'known', duration: result.duration });
      } else {
        setState({ status: 'unknown' });
      }
    });

    return () => { cancelled = true; };
  }, [video, enabled]);

  if (video.duration) return { status: 'known', duration: video.duration };
  if (isYouTubeUrl(video.videoUrl)) return { status: 'unknown' };
  return state;
}
